import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["isOpen", "onClose"];
import { Toast } from 'drywall';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { STATUS_TYPES } from '../../constants';
import { AlertMixin } from '../../mixins/components/AlertMixin';
import { statusIconAppearance } from '../../util/statusIconAppearance';

/**
 * @extends Toast
 * @since 7.21.0
 */
var Alert = /*#__PURE__*/styled(function (_ref) {
  var isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    openState = _useState2[0],
    setOpenState = _useState2[1];
  var customizedOnClose = useCallback(function () {
    setOpenState(false);
    if (onClose) {
      onClose();
    }
  }, [setOpenState, onClose]);
  if (
  // Controlled closed
  typeof isOpen === 'boolean' && !isOpen ||
  // Uncontrolled closed
  typeof isOpen !== 'boolean' && !openState) {
    return null;
  }

  // props onClose and isOpen will not bleed to Toast
  return /*#__PURE__*/React.createElement(Toast, _extends({
    onClose: customizedOnClose
  }, props));
}).withConfig({
  displayName: "Alert",
  componentId: "c11n-8-111-1__sc-1k2e36i-0"
})(["", ""], AlertMixin);
Alert.displayName = 'Alert';
Alert.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An optional `TextButton` action.
   *
   * @ignoreInControls
   */
  actionButton: PropTypes.node,
  /**
   * The type of alert to display. By default, `appearance` will also modify the `icon` used.
   */
  appearance: PropTypes.oneOf(Object.values(STATUS_TYPES)).isRequired,
  /**
   * The body content of the alert.
   */
  body: PropTypes.node,
  /**
   * In general, you will want to use the `body` prop,
   * but you can use the children prop to override the default component layout.
   * When passing a function, the function will receive all the component props as the only
   * argument (Note: some of the props may have been modified and/or enhanced,
   * so you should always use the values that are passed to the render function).
   *
   * @ignoreInControls
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The close button node. You can set this to `<CloseButton />` if needed.
   * Usability Hint: Enabling the close button is not recommended if there is
   * an appropriate action to help revolve warnings or errors instead.
   *
   * @ignoreInControls
   */
  closeButton: PropTypes.node,
  /**
   * Alerts are an [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html)
   * component by default. You can specify `isOpen` to manually control the visibility of the
   * component.
   *
   * @ignoreInControls
   */
  isOpen: PropTypes.bool,
  /**
   * The icon node. By default, this will use an icon based on the alert `appearance`.
   *
   * @ignoreInControls
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Alerts should have a role of [status](https://www.w3.org/TR/wai-aria-1.1/#status) or
   * [alert](https://www.w3.org/TR/wai-aria-1.1/#alert) to signal to assistive technologies
   * that it requires the user's attention. In general, you will want to use the less noisy
   * "status" role.
   */
  role: PropTypes.oneOf(['status', 'alert'])
} : {};
Alert.defaultProps = {
  appearance: 'info',
  closeButton: null,
  icon: statusIconAppearance,
  isOpen: null,
  role: 'status'
};

/** @component */
export { Alert };